import React, { useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { User, ShoppingCart, FileText, LogOut } from 'lucide-react'
import CustomersEntity from '@/entities/api/customers/customer'
import { API_URL } from '@/services/api/keys'

interface ProfilePopupProps {
	isOpen: boolean
	onClose: () => void
	customer: CustomersEntity
	totalItems: number
	onLogout: () => void
}

const ProfilePopup: React.FC<ProfilePopupProps> = ({
	isOpen,
	onClose,
	customer,
	totalItems,
	onLogout,
}) => {
	const popupRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				popupRef.current &&
				!popupRef.current.contains(event.target as Node)
			) {
				onClose()
			}
		}

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose()
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
			document.addEventListener('keydown', handleEscape)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isOpen, onClose])

	if (!isOpen) return null

	const getInitials = (name: string, lastname: string) => {
		return `${name.charAt(0)}${lastname.charAt(0)}`.toUpperCase()
	}

	const handleLogout = () => {
		onLogout()
		onClose()
	}

	return (
		<div
			ref={popupRef}
			className='absolute right-0 top-full mt-2 w-80 sm:w-[25vw] md:w-[25vw] lg:w-[22vw] xl:w-[20vw] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[999999] transform opacity-100 scale-100 animate-in slide-in-from-top-2 duration-200'
			style={{
				boxShadow:
					'0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
			}}
		>
			<div className='relative'>
				<div className='relative pt-8 pb-6 px-6 bg-gradient-to-r from-red-500 to-red-600'>
					<div className='flex items-center space-x-4'>
						<div className='relative'>
							{customer.images && customer.images.length > 0 ? (
								<img
									src={`${API_URL}/${customer.images[0].url}`}
									alt={`${customer.name} ${customer.lastname}`}
									className='w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg'
								/>
							) : (
								<div className='w-16 h-16 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 border-4 border-white shadow-lg flex items-center justify-center'>
									<span className='text-white font-bold text-lg'>
										{getInitials(customer.name, customer.lastname)}
									</span>
								</div>
							)}
							<div className='absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full'></div>
						</div>

						<div className='flex-1 min-w-0'>
							<h3 className='text-lg font-bold text-white truncate'>
								{customer.name} {customer.lastname}
							</h3>
							<p className='text-sm text-red-100 truncate'>{customer.email}</p>
							<div className='flex items-center mt-1'>
								<div className='w-2 h-2 bg-green-400 rounded-full mr-2'></div>
								<span className='text-xs text-green-200 font-medium'>
									En línea
								</span>
							</div>
						</div>
					</div>
				</div>

				<div className='px-2 pb-2 mt-4'>
					<div className='space-y-1'>
						<Link
							to='/Perfil/informacion'
							onClick={onClose}
							className='flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-200 group'
						>
							<div className='w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors duration-200'>
								<User className='w-5 h-5' />
							</div>
							<div>
								<span className='font-semibold text-sm'>Mi Perfil</span>
								<p className='text-xs text-gray-500 group-hover:text-red-500'>
									Gestionar información personal
								</p>
							</div>
						</Link>

						<Link
							to='/Carrito'
							className='w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-200 group'
						>
							<div className='w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors duration-200 relative'>
								<ShoppingCart className='w-5 h-5' />
								{totalItems > 0 && (
									<span className='absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center font-bold'>
										{totalItems > 9 ? '9+' : totalItems}
									</span>
								)}
							</div>
							<div>
								<span className='font-semibold text-sm'>
									Carrito de Compras
								</span>
								<p className='text-xs text-start text-gray-500 group-hover:text-red-500'>
									{totalItems > 0
										? `${totalItems} producto${totalItems > 1 ? 's' : ''}`
										: 'Sin productos'}
								</p>
							</div>
						</Link>

						<Link
							to='/Perfil/compras'
							onClick={onClose}
							className='flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-200 group'
						>
							<div className='w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors duration-200'>
								<FileText className='w-5 h-5' />
							</div>
							<div>
								<span className='font-semibold text-sm'>
									Historial de Compras
								</span>
								<p className='text-xs text-gray-500 group-hover:text-red-500'>
									Ver pedidos anteriores
								</p>
							</div>
						</Link>
					</div>

					<div className='mt-4 pt-4 border-t border-gray-100'>
						<button
							onClick={handleLogout}
							className='w-full cursor-pointer flex items-center justify-center space-x-2 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 group font-semibold'
						>
							<LogOut className='w-5 h-5 group-hover:scale-110 transition-transform duration-200' />
							<span>Cerrar Sesión</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default ProfilePopup
